/**
 * Why a signature read on the sheet is flagged, in decreasing severity. Derived from the proposal —
 * the API returns no status of its own, so `useAttendanceReview` is the single place this is decided.
 */
export enum AttendanceSignatureStatusEnum {
  /** Nothing on the platform matches the name read on the sheet: the admin picks the collaborator. */
  UNRECOGNIZED = "unrecognized",
  /** The backend's second pass had a model propose this pairing — the admin confirms the identity. */
  AI_SUGGESTED = "ai_suggested",
  /** Resolved, but not booked on the session: confirming the attendance adds them to it. */
  NOT_ENROLLED = "not_enrolled",
  /** Booked on the session and matched on the sheet, nothing to check. */
  MATCHED = "matched",
}

/** The two groups the participant picker offers, in the order it lists them. */
export enum AttendanceUserGroupEnum {
  /** Everyone booked on the session — what the sheet's names are most likely to be. */
  SESSION = "session",
  /** The rest of the company, searched server-side. */
  COMPANY = "company",
}

/**
 * How the backend tied a sheet line to a collaborator. Sent on every line of the three buckets;
 * `null` on the two that matched nobody (`notFoundInSession`, `notFoundInFile`).
 */
export enum AttendanceNameMatchEnum {
  /** Deterministic match — same tokens in any order, accents and case folded. Backend is certain. */
  EXACT = "exact",
  /** A model proposed the pairing and code re-verified it; only a human may confirm it. */
  AI_SUGGESTED = "ai_suggested",
}

/** Which screen the attendance workflow is waiting on. */
export enum AttendanceRunStatusEnum {
  PROPOSAL = "proposal",
  SESSION_CHOICE = "session_choice",
}

/** Route query used to restore an attendance run after a browser reload. */
export enum AttendanceRouteQueryEnum {
  RUN_ID = "attendanceRunId",
  /**
   * The restored run is waiting in the conversation rather than on screen — a session to check
   * before the attendance opens, or a picker to answer. Without it a reload would reopen the
   * review on a session nobody has confirmed, which is the check this flow exists for.
   */
  AWAITING_CHECK = "attendanceAwaitingCheck",
}

/**
 * What a streamed sheet import ended on. The session-page upload shows the review on a proposal and
 * says why on anything else — it has no conversation to fall back to.
 */
export enum AttendanceImportOutcomeEnum {
  /** The attendance is pre-filled: the review screen takes over. */
  PROPOSAL = "proposal",
  /** The sheet was read, but matched a session other than the one it was imported from. */
  SESSION_CHOICE = "session_choice",
  /** The assistant named a failure of its own. */
  ERROR = "error",
  /** The answer declared nothing — the tool was never called. */
  NONE = "none",
}

/**
 * Which screen started the attendance run. The two share the review, the store and the workflow, but
 * not the assistant panel: a sheet imported from a session's steps goes through a one-shot
 * conversation the panel never shows, so a card offered there would stand alone in the middle of an
 * empty conversation.
 */
export enum AttendanceRunOriginEnum {
  /** Dropped in the Copilote. Its answer hosts the cards, and the conversation is the way back in. */
  CHAT = "chat",
  /** « Importer la feuille d'émargement » on a session's steps — that screen is the whole flow. */
  SESSION_UPLOAD = "session_upload",
}

/** How a read-back of a persisted run ended — only a real failure is worth telling the admin about. */
export enum AttendanceRunLoadEnum {
  LOADED = "loaded",
  /** A later request claimed the screen while this one was in flight; its answer is dropped. */
  SUPERSEDED = "superseded",
  FAILED = "failed",
}
